import { useState, useCallback, useRef } from "react"
import { Bug, Lightbulb, TrendingUp, HelpCircle, X, ImagePlus, Loader2 } from "lucide-react"
import { toast } from "sonner"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"
import { useFeedbackStore, type FeedbackCategory } from "@/stores/feedback-store"

// ===================
// Constants
// ===================

const MAX_SCREENSHOTS = 5

const categories: Array<{ value: FeedbackCategory; label: string; icon: typeof Bug }> = [
  { value: "bug", label: "Bug", icon: Bug },
  { value: "feature-request", label: "Feature Request", icon: Lightbulb },
  { value: "improvement", label: "Improvement", icon: TrendingUp },
  { value: "question", label: "Question", icon: HelpCircle },
]

// ===================
// Types
// ===================

interface ScreenshotPreview {
  id: string
  base64: string
  mimeType: string
  preview: string
}

// ===================
// Component
// ===================

export function FeedbackDialog() {
  const { isOpen, isSubmitting, error, close, submitFeedback } = useFeedbackStore()

  const [category, setCategory] = useState<FeedbackCategory>("bug")
  const [message, setMessage] = useState("")
  const [screenshots, setScreenshots] = useState<ScreenshotPreview[]>([])
  const [validationError, setValidationError] = useState<string | null>(null)

  const fileInputRef = useRef<HTMLInputElement>(null)

  const resetForm = useCallback(() => {
    setCategory("bug")
    setMessage("")
    setScreenshots([])
    setValidationError(null)
  }, [])

  const handleOpenChange = useCallback((open: boolean) => {
    if (!open) {
      close()
      resetForm()
    }
  }, [close, resetForm])

  const addScreenshot = useCallback((file: File) => {
    if (!file.type.startsWith("image/")) return

    if (file.size > 5 * 1024 * 1024) {
      setValidationError("Each screenshot must be under 5MB")
      return
    }

    setScreenshots((prev) => {
      if (prev.length >= MAX_SCREENSHOTS) {
        setValidationError(`Maximum ${MAX_SCREENSHOTS} screenshots allowed`)
        return prev
      }

      const reader = new FileReader()
      reader.onload = () => {
        const dataUrl = reader.result as string
        const base64 = dataUrl.split(",")[1]!
        setScreenshots((current) => {
          if (current.length >= MAX_SCREENSHOTS) return current
          return [
            ...current,
            {
              id: crypto.randomUUID(),
              base64,
              mimeType: file.type,
              preview: dataUrl,
            },
          ]
        })
        setValidationError(null)
      }
      reader.readAsDataURL(file)
      return prev
    })
  }, [])

  const removeScreenshot = useCallback((id: string) => {
    setScreenshots((prev) => prev.filter((s) => s.id !== id))
  }, [])

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items
    if (!items) return

    for (const item of items) {
      if (item.type.startsWith("image/")) {
        e.preventDefault()
        const file = item.getAsFile()
        if (file) addScreenshot(file)
        return
      }
    }
  }, [addScreenshot])

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files) {
      for (const file of files) {
        addScreenshot(file)
      }
    }
    e.target.value = ""
  }, [addScreenshot])

  const handleSubmit = useCallback(async () => {
    if (!message.trim()) {
      setValidationError("Please describe your feedback")
      return
    }

    const payload = {
      category,
      message: message.trim(),
      pageUrl: window.location.href,
      userAgent: navigator.userAgent,
      ...(screenshots.length > 0 ? {
        screenshots: screenshots.map((s) => ({
          base64: s.base64,
          mimeType: s.mimeType,
        })),
      } : {}),
    }

    const success = await submitFeedback(payload)

    if (success) {
      toast.success("Thanks for your feedback!")
      resetForm()
    }
  }, [category, message, screenshots, submitFeedback, resetForm])

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-sm font-medium">Send Feedback</DialogTitle>
          <DialogDescription className="text-meta">
            Help us improve BidContext. Your feedback goes directly to the team.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2" onPaste={handlePaste}>
          {/* Category */}
          <div className="space-y-1.5">
            <Label className="text-meta">Category</Label>
            <Select value={category} onValueChange={(v) => setCategory(v as FeedbackCategory)}>
              <SelectTrigger className="h-9 rounded-lg">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    <div className="flex items-center gap-2">
                      <cat.icon className="h-4 w-4 text-muted-foreground" />
                      <span>{cat.label}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Message */}
          <div className="space-y-1.5">
            <Label className="text-meta">Message</Label>
            <Textarea
              placeholder="What's on your mind?"
              value={message}
              onChange={(e) => {
                setMessage(e.target.value)
                if (validationError) setValidationError(null)
              }}
              className="min-h-[120px] rounded-lg resize-none"
              maxLength={5000}
            />
          </div>

          {/* Screenshots */}
          <div className="space-y-1.5">
            <Label className="text-meta">
              Screenshots (optional, up to {MAX_SCREENSHOTS})
            </Label>

            {/* Preview grid */}
            {screenshots.length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {screenshots.map((s) => (
                  <div key={s.id} className="relative rounded-lg border border-border overflow-hidden group">
                    <img
                      src={s.preview}
                      alt="Screenshot"
                      className="w-full h-20 object-cover bg-muted/30"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute top-0.5 right-0.5 h-5 w-5 bg-background/80 hover:bg-background opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => removeScreenshot(s.id)}
                      aria-label="Remove screenshot"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Add button */}
            {screenshots.length < MAX_SCREENSHOTS && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className={cn(
                  "w-full rounded-lg border border-dashed border-border",
                  "flex items-center justify-center gap-1.5",
                  screenshots.length > 0 ? "h-10" : "h-20 flex-col",
                  "text-muted-foreground hover:border-foreground/30 hover:text-foreground transition-colors"
                )}
              >
                <ImagePlus className={screenshots.length > 0 ? "h-4 w-4" : "h-5 w-5"} />
                <span className="text-xs">
                  {screenshots.length > 0 ? "Add another screenshot" : "Click to attach or paste screenshots"}
                </span>
              </button>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              multiple
              className="hidden"
              onChange={handleFileChange}
            />
          </div>

          {/* Error */}
          {(validationError || error) && (
            <p className="text-xs text-destructive">{validationError || error}</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => handleOpenChange(false)} disabled={isSubmitting} className="rounded-lg">
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={isSubmitting} className="rounded-lg">
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              "Send Feedback"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
